/**
 * @fileoverview Compact processing-status pill for the Library toolbar.
 *
 * Replaces the retired BulkProcessingStatus bar. Hidden when nothing is
 * in flight and nothing has failed; otherwise shows a spinner with the
 * in-flight count (or a failure count when only failures remain), with a
 * popover breaking down Ready / Processing / Queued / Failed.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { AlertCircle, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";

interface ProcessingPillProps {
  jobId: Id<"jobs">;
}

/**
 * Toolbar pill summarising the job's document-processing pipeline.
 * Renders nothing while everything is ready (or the job has no documents).
 */
export function ProcessingPill({ jobId }: ProcessingPillProps) {
  const { data: stats } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.extraction.queries.getJobProcessingStats,
      { jobId },
    ),
  );

  if (!stats || stats.total === 0) return null;

  // "In flight" covers actively-processing docs plus queued (pending) ones.
  const inFlight = stats.processing + stats.pending;
  if (inFlight === 0 && stats.failed === 0) return null;

  const failedOnly = inFlight === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 gap-1.5 bg-background px-2 text-xs"
        >
          {failedOnly ? (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500" />
              <span className="text-amber-600 dark:text-amber-500">
                {stats.failed} failed
              </span>
            </>
          ) : (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600 dark:text-blue-400" />
              {inFlight} processing
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Processing status — {stats.ready} of {stats.total} ready
        </p>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-3 w-3" />
              Ready
            </span>
            <span className="tabular-nums">{stats.ready}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Processing
            </span>
            <span className="tabular-nums">{stats.processing}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3 w-3" />
              Queued
            </span>
            <span className="tabular-nums">{stats.pending}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-destructive">
              <AlertCircle className="h-3 w-3" />
              Failed
            </span>
            <span className="tabular-nums">{stats.failed}</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
